import { Stack, TextField } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import CompanySelect from "../doc-scanning/company-select";

const styles = {
  root: { maxWidth: 560 },
} satisfies Record<string, SxProps<Theme>>;

/** Props for {@link PieceBackfillSetup}. */
export type PieceBackfillSetupProps = {
  /** The selected company's uuid, or null when none is selected. */
  readonly companyUuid: string | null;
  /** Called with the selected company's uuid and name, or nulls when cleared. */
  readonly onCompanyChange: (
    companyUuid: string | null,
    companyName: string | null,
  ) => void;
  /** The eligibility cutoff as a `YYYY-MM-DD` string. */
  readonly cutoffDate: string;
  /** Called with the new cutoff as a `YYYY-MM-DD` string. */
  readonly onCutoffDateChange: (cutoffDate: string) => void;
  /** The operator's note, stored with the run. */
  readonly notes: string;
  /** Called with the new note text. */
  readonly onNotesChange: (notes: string) => void;
  /** True while a run is in flight, which freezes the parameters. */
  readonly disabled: boolean;
};

/**
 * Parameters panel: which company to backfill, how far back to look, and an
 * optional note stored with the run for the operator's own audit trail.
 *
 * Changing anything here invalidates a completed dry run, which is why the whole
 * panel is disabled while a run is in flight.
 */
const PieceBackfillSetup = ({
  companyUuid,
  onCompanyChange,
  cutoffDate,
  onCutoffDateChange,
  notes,
  onNotesChange,
  disabled,
}: PieceBackfillSetupProps) => {
  return (
    <Stack spacing={2.5} sx={styles.root}>
      <CompanySelect
        currentCompanyUuid={companyUuid}
        onChange={(option) => {
          onCompanyChange(option?.value ?? null, option?.label ?? null);
        }}
      />

      <TextField
        type="date"
        label="Eligibility cutoff"
        value={cutoffDate}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        helperText="Only orders marked on hand (or, absent that, created) on or after this date are in scope. Widen it if the tenant has older packages that still need pieces."
        onChange={(event) => {
          onCutoffDateChange(event.target.value);
        }}
      />

      <TextField
        multiline
        label="Note (optional)"
        value={notes}
        disabled={disabled}
        minRows={2}
        helperText="Stored with the run. Say why this backfill is being done, so the next operator reading the history knows."
        onChange={(event) => {
          onNotesChange(event.target.value);
        }}
      />
    </Stack>
  );
};

export default PieceBackfillSetup;
